import React, { useState } from 'react';
import { Award, BookOpen, Target, Users, Eye, Heart, Zap, ChevronRight, GraduationCap, Trophy, TrendingUp, Globe } from 'lucide-react';

const AboutSection = () => {
  const [activeTab, setActiveTab] = useState('vision');

  const stats = [
    { number: '25+', label: 'Years of Excellence', icon: Award },
    { number: '5000+', label: 'Alumni Network', icon: Users },
    { number: '50+', label: 'Expert Faculty', icon: BookOpen },
    { number: '100%', label: 'Placement Assistance', icon: Target },
  ];

  const values = [
    {
      icon: Eye,
      title: 'Vision',
      content: 'To provide quality education and training to nurture the Indian minds, which play a significant role in the personal and intellectual development of our students\' career, and furthermore our country.',
      color: 'from-blue-600 to-blue-400'
    },
    {
      icon: Target,
      title: 'Mission',
      content: 'To enable students to understand the rapidly changing global economy, prepare confident professionals, and instill entrepreneurship while providing opportunities to both urban and rural promising candidates.',
      color: 'from-orange-500 to-orange-400'
    },
    {
      icon: Heart,
      title: 'Values',
      content: 'Excellence, integrity, innovation, and social responsibility form the core of our educational philosophy, shaping students into refined citizens with deep ethical sense.',
      color: 'from-green-500 to-green-400'
    }
  ];

  const objectives = [
    {
      icon: GraduationCap,
      title: "Academic Excellence",
      description: "Foster academic rigor and intellectual curiosity through innovative teaching methodologies.",
      color: "from-blue-500 to-cyan-400"
    },
    {
      icon: Trophy,
      title: "Skill Development",
      description: "Bridge the gap between academia and industry through practical skill enhancement programs.",
      color: "from-orange-500 to-red-400"
    },
    {
      icon: TrendingUp,
      title: "Career Growth",
      description: "Prepare students for global opportunities with comprehensive career guidance and support.",
      color: "from-green-500 to-emerald-400"
    },
    {
      icon: Globe,
      title: "Global Perspective",
      description: "Develop international outlook through exposure to global best practices and collaborations.",
      color: "from-purple-500 to-pink-400"
    }
  ];

  return (
    <div className="bg-white">
      <div className="relative bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-blue-200 mb-3 md:mb-4">
            <span className="hover:text-orange-400 cursor-pointer transition-colors">Home</span>
            <ChevronRight size={14} className="sm:w-4 sm:h-4" />
            <span className="text-orange-400 font-semibold">About Us</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 md:mb-4">About Us</h1>
          <p className="text-base sm:text-lg md:text-xl text-blue-100 max-w-2xl">Empowering minds, shaping futures since 1995</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl p-4 sm:p-6 text-center transform hover:scale-105 transition-all duration-300">
              <div className="inline-flex p-2 sm:p-3 bg-gradient-to-br from-orange-50 to-orange-100 rounded-full mb-2 sm:mb-3">
                <stat.icon className="w-4 h-4 sm:w-6 sm:h-6 text-orange-500" />
              </div>
              <div className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-900">{stat.number}</div>
              <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-start">
          <div className="space-y-4 sm:space-y-6">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-16 h-16 sm:w-24 sm:h-24 bg-orange-100 rounded-full -z-10"></div>
              <div className="absolute -bottom-4 -right-4 w-20 h-20 sm:w-32 sm:h-32 bg-blue-100 rounded-full -z-10"></div>
                <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl group cursor-pointer">
                <img 
                  src="image1.png" 
                  alt="RDS College Campus" 
                  className="w-full h-[200px] sm:h-[250px] md:h-[300px] object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/70 to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2">Welcome to RDS</h3>
                  <p className="text-xs sm:text-sm md:text-base text-blue-100">Where excellence meets opportunity</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg group cursor-pointer">
                <img 
                  src="image2.png" 
                  alt="Students" 
                  className="w-full h-24 sm:h-32 md:h-40 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg group cursor-pointer">
                <img 
                  src="image4.png" 
                  alt="Library" 
                  className="w-full h-24 sm:h-32 md:h-40 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="col-span-2 rounded-lg sm:rounded-xl overflow-hidden shadow-md sm:shadow-lg group cursor-pointer">
                <img 
                  src="image6.png" 
                  alt="Graduation" 
                  className="w-full h-32 sm:h-40 md:h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
          <div className="space-y-6 md:space-y-8">
            <div className="relative">
              <div className="absolute -top-4 -left-4 sm:-top-6 sm:-left-6 text-4xl sm:text-6xl text-orange-200">"</div>
              <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed pl-4 sm:pl-6 italic">
                We, in RDS Group of Colleges, have a distinct objective that sets us apart from other institutions of the region. The global scenario is changing fast and there is a great demand for professionals who can keep pace with this change.
              </p>
            </div>
            <div className="flex overflow-x-auto pb-2 space-x-1 sm:space-x-2 border-b border-gray-200 scrollbar-hide">
              {values.map((value, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(value.title.toLowerCase())}
                  className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all duration-300 whitespace-nowrap relative ${
                    activeTab === value.title.toLowerCase()
                      ? 'text-orange-500'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <value.icon size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span>{value.title}</span>
                  {activeTab === value.title.toLowerCase() && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-orange-500"></div>
                  )}
                </button>
              ))}
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl shadow-md sm:shadow-lg">
              {activeTab === 'vision' && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-600 to-blue-400 rounded-lg sm:rounded-xl">
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">Our Vision</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {values[0].content}
                  </p>
                </div>
              )}

              {activeTab === 'mission' && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="p-2 sm:p-3 bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg sm:rounded-xl">
                      <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">Our Mission</h3>
                  </div>
                  <ul className="space-y-2 sm:space-y-3">
                    <li className="flex items-start space-x-2 sm:space-x-3">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-orange-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                      <p className="text-sm sm:text-base text-gray-700">Enable students to understand the rapidly changing global economy</p>
                    </li>
                    <li className="flex items-start space-x-2 sm:space-x-3">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-orange-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                      <p className="text-sm sm:text-base text-gray-700">Prepare confident, creative, and proactive professionals</p>
                    </li>
                    <li className="flex items-start space-x-2 sm:space-x-3">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-orange-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                      <p className="text-sm sm:text-base text-gray-700">Develop high-value academicians and corporate professionals</p>
                    </li>
                    <li className="flex items-start space-x-2 sm:space-x-3">
                      <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-orange-500 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                      <p className="text-sm sm:text-base text-gray-700">Instill entrepreneurship and provide opportunities to all</p>
                    </li>
                  </ul>
                </div>
              )}

              {activeTab === 'values' && (
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                    <div className="p-2 sm:p-3 bg-gradient-to-r from-green-500 to-green-400 rounded-lg sm:rounded-xl">
                      <Heart className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-blue-900">Our Values</h3>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {values[2].content}
                  </p>
                </div>
              )}
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border-l-4 border-orange-500">
              <p className="text-sm sm:text-base text-gray-700 italic mb-3 sm:mb-4">
                "I am proud that RDS Group of Colleges enables our students to stand high in knowledge and character, in today's challenging world. I heartily welcome our students and wish them the very best for a glorious future."
              </p>
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-blue-900 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-xl">
                  DR
                </div>
                <div className="ml-2 sm:ml-3">
                  <h4 className="font-bold text-blue-900 text-sm sm:text-base">Dr. R.D. Singh</h4>
                  <p className="text-xs sm:text-sm text-gray-600">Chairman, RDS Group of Colleges</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="relative bg-gradient-to-br from-slate-50 via-white to-orange-50 py-12 md:py-20 overflow-hidden">
        <div className="hidden md:block absolute top-0 left-0 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="hidden md:block absolute top-0 right-0 w-64 h-64 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="hidden md:block absolute bottom-0 left-20 w-64 h-64 bg-purple-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <span className="inline-block px-3 py-1 md:px-4 md:py-2 bg-orange-100 text-orange-600 rounded-full text-xs md:text-sm font-semibold mb-3 md:mb-4">
              What Drives Us
            </span>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-3 md:mb-4">
              Our Core <span className="text-orange-500">Objectives</span>
            </h2>
            <div className="w-16 md:w-24 h-1 bg-gradient-to-r from-orange-500 to-blue-500 mx-auto rounded-full"></div>
            <p className="text-sm md:text-base text-gray-600 mt-4 md:mt-6 max-w-2xl mx-auto px-4">
              Committed to excellence in education and holistic development of every student
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8 px-4 md:px-0">
            {objectives.map((objective, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-xl md:rounded-2xl shadow-lg md:shadow-xl hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${objective.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                <div className="relative p-5 md:p-8 z-10">
                  <div className={`w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br ${objective.color} rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-md md:shadow-lg`}>
                    <objective.icon className="w-5 h-5 md:w-8 md:h-8 text-white" />
                  </div>
                    <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-800 mb-2 md:mb-3 group-hover:text-white transition-colors duration-300">
                    {objective.title}
                  </h3>
                  
                  <p className="text-xs md:text-sm text-gray-600 group-hover:text-white/90 transition-colors duration-300 leading-relaxed">
                    {objective.description}
                  </p>
                  <div className={`absolute bottom-0 left-0 w-0 h-1 bg-gradient-to-r ${objective.color} group-hover:w-full transition-all duration-500`}></div>
                </div>
                  <div className="hidden md:block absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>
            ))}
          </div>
          <div className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 px-4 md:px-0">
            <div className="text-center">
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-blue-900 mb-1">15+</div>
              <div className="text-xs md:text-sm text-gray-600">Courses Offered</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-orange-500 mb-1">100%</div>
              <div className="text-xs md:text-sm text-gray-600">Placement Record</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-blue-900 mb-1">50+</div>
              <div className="text-xs md:text-sm text-gray-600">Industry Partners</div>
            </div>
            <div className="text-center">
              <div className="text-xl md:text-2xl lg:text-3xl font-bold text-orange-500 mb-1">24/7</div>
              <div className="text-xs md:text-sm text-gray-600">Learning Support</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutSection;